import type { QuaternionWxyz } from "./orientation";
import type { QuaternionSignalModalityV1, SignalProfileV1 } from "@rekonas/domain";

export interface OrientationModelConfig {
  id: "head" | "torso";
  displayName: string;
  modalityIds: readonly string[];
  assetUrl: string;
  calibrationVersion: string;
  calibrationWxyz: QuaternionWxyz;
  meshAlignmentXDegrees: number;
  pivot: readonly [number, number, number];
  normalizedSize: number;
  provisional: boolean;
}

const calibration: QuaternionWxyz = [Math.SQRT1_2, -Math.SQRT1_2, 0, 0];

export const ORIENTATION_MODELS: readonly OrientationModelConfig[] = [
  {
    id: "head",
    displayName: "Head orientation",
    modalityIds: ["h1.quaternion", "somnolinc-head-1.quat"],
    assetUrl: new URL("./assets/head.glb", import.meta.url).href,
    calibrationVersion: "somnolinc-head-scene-v1",
    calibrationWxyz: calibration,
    meshAlignmentXDegrees: 0,
    pivot: [0, 45, 98.87004],
    normalizedSize: 1.05,
    provisional: false,
  },
  {
    id: "torso",
    displayName: "Torso orientation",
    modalityIds: ["c1.quaternion", "aeruna-chest-1.quat"],
    assetUrl: new URL("./assets/torso.glb", import.meta.url).href,
    calibrationVersion: "aeruna-chest-scene-provisional-v1",
    calibrationWxyz: calibration,
    meshAlignmentXDegrees: -15.77542,
    pivot: [-15, -79.787155, -17.260399],
    normalizedSize: 1.25,
    provisional: true,
  },
];

export function orientationModelForModality(
  modalityId: string,
): OrientationModelConfig | undefined {
  return ORIENTATION_MODELS.find((item) => item.modalityIds.includes(modalityId));
}

export function profileWithOrientationModels(profile: SignalProfileV1): SignalProfileV1 {
  return {
    ...profile,
    modalities: profile.modalities.map((modality) => {
      const config = orientationModelForModality(modality.id);
      if (!config || modality.kind === "quaternion" || modality.kind !== "multichannel") return modality;
      const component = (suffix: string) => modality.aliases.filter((alias) =>
        alias.toUpperCase().endsWith(`_${suffix}`),
      );
      const components = { w: component("W"), x: component("X"), y: component("Y"), z: component("Z") };
      if (Object.values(components).some((aliases) => aliases.length !== 1)) return modality;
      const converted: QuaternionSignalModalityV1 = {
        id: modality.id,
        label: config.displayName,
        kind: "quaternion",
        required: modality.required,
        origin: modality.origin,
        order: modality.order,
        fallback: modality.fallback,
        unit: "1",
        components,
        sampling: "native_shared_clock",
        viewer: "orientation3d",
        overview: "none",
        quaternion: {
          order: "wxyz",
          rotation: "active_body_to_world",
          handedness: "right",
          world_axes: "x-right_y-up_z-toward-viewer",
          body_axes: "x-right_y-up_z-forward",
          source_to_world_wxyz: [1, 0, 0, 0],
          model_to_body_wxyz: [1, 0, 0, 0],
          max_gap_seconds: 0.2,
        },
      };
      return converted;
    }),
  };
}
